import React, { useState, useRef, useEffect } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import PaymentIcon from '@material-ui/icons/Payment';
import UIContainer from '../UI/Container/UIContainer';
import { usePOS } from '../../contexts/POSContext';

const POSScannerBar = ({
  onOpenSearch,
  onOpenCustomer,
  onOpenCoupon,
  onOpenDiscount,
  onOpenCheckout,
}) => {
  const { addItemByBarcode, cartItems } = usePOS();
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanFeedback, setScanFeedback] = useState(null);
  const inputRef = useRef(null);

  // Mantém o foco sempre no campo de bipagem
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    focusInput();

    // Auto-focus inteligente ao clicar na tela quando nenhum modal estiver aberto
    const handleGlobalClick = (e) => {
      const isInDialog = Boolean(e.target.closest('.MuiDialog-root') || e.target.closest('.MuiMenu-paper'));
      const isOtherInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT';
      if (!isInDialog && !isOtherInput) {
        focusInput();
      }
    };

    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanSubmit();
    }
  };

  const handleScanSubmit = () => {
    const rawVal = barcodeInput.trim();
    if (!rawVal) {
      if (cartItems.length > 0 && onOpenCheckout) {
        onOpenCheckout();
      } else if (onOpenSearch) {
        onOpenSearch('');
      }
      return;
    }

    let multiplier = 1;
    let code = rawVal;

    // Suporte a multiplicador exclusivo por asterisco '*' (ex: 0.450*789123 ou 0,450*789123)
    if (rawVal.includes('*')) {
      const parts = rawVal.split('*');
      if (parts.length === 2) {
        const parsedMult = parseFloat(parts[0].replace(',', '.'));
        if (!isNaN(parsedMult) && parsedMult > 0) {
          multiplier = parsedMult;
          code = parts[1].trim();
        }
      }
    }

    const result = addItemByBarcode(code, multiplier);

    if (result.success) {
      const finalQty = result.amount || multiplier;
      const formattedQty = Number(finalQty).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
      setScanFeedback({ type: 'success', text: `+ ${formattedQty}x ${result.item.name}` });
      setBarcodeInput('');
    } else if (result.multipleMatches) {
      if (onOpenSearch) onOpenSearch(result.searchTerm);
      setBarcodeInput('');
    } else {
      setScanFeedback({ type: 'error', text: result.message || 'Código não encontrado!' });
    }

    setTimeout(() => {
      setScanFeedback(null);
    }, 2500);

    focusInput();
  };

  return (
    <UIContainer container style={{ minHeight: 'auto', padding: '12px 16px', marginBottom: 0 }}>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Campo de Leitura / Bipagem Rápida */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: 6,
              padding: '4px 12px',
              transition: 'border-color 0.2s',
            }}
          >
            <SearchIcon style={{ color: '#64748b', marginRight: 8, fontSize: 20 }} />
            <input
              ref={inputRef}
              id="sale-pos-barcode-input"
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontSize: '0.95rem',
                color: '#1e293b',
                fontWeight: 500,
              }}
              placeholder="Passe o leitor ou digite o código de barras"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
          </div>

          <button
            type="button"
            onClick={handleScanSubmit}
            style={{
              backgroundColor: '#17497F',
              color: '#ffffff',
              border: 'none',
              borderRadius: 6,
              padding: '9px 18px',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            Inserir
          </button>
        </div>

        {/* Guia de Atalhos e Feedback de Bipagem */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="pos-shortcuts-strip" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
            <button
              type="button"
              className="pos-shortcut-btn"
              onClick={() => onOpenSearch(barcodeInput)}
            >
              <SearchIcon style={{ fontSize: 15 }} /> Pesquisar Item <span className="pos-key-badge">F2</span>
            </button>

            <button
              type="button"
              className="pos-shortcut-btn"
              onClick={onOpenCustomer}
            >
              <PersonIcon style={{ fontSize: 15 }} /> Identificar Cliente <span className="pos-key-badge">F7</span>
            </button>

            <button
              type="button"
              className="pos-shortcut-btn"
              onClick={onOpenCoupon}
            >
              <LocalOfferIcon style={{ fontSize: 15 }} /> Promoção / Cupom <span className="pos-key-badge">F8</span>
            </button>

            <button
              type="button"
              className="pos-shortcut-btn"
              onClick={onOpenDiscount}
            >
              <LoyaltyIcon style={{ fontSize: 15 }} /> Desconto <span className="pos-key-badge">F9</span>
            </button>
          </div>

          {scanFeedback ? (
            <div
              style={{
                fontSize: '0.82rem',
                fontWeight: 700,
                color: scanFeedback.type === 'success' ? '#1b5e20' : '#c62828',
                padding: '3px 10px',
                borderRadius: 4,
                backgroundColor: scanFeedback.type === 'success' ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${scanFeedback.type === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
              }}
            >
              {scanFeedback.text}
            </div>
          ) : (
            <button
              type="button"
              className="pos-shortcut-btn"
              style={{ backgroundColor: '#e8f5e9', borderColor: '#c8e6c9', color: '#185421' }}
              onClick={() => {
                if (cartItems.length > 0 && onOpenCheckout) onOpenCheckout();
              }}
            >
              <PaymentIcon style={{ fontSize: 15 }} /> Fechar Venda <span className="pos-key-badge" style={{ color: '#185421', borderColor: '#a5d6a7' }}>F10 / Enter</span>
            </button>
          )}
        </div>
      </div>
    </UIContainer>
  );
};

export default POSScannerBar;
